import type { EditorState } from '@atlaskit/editor-prosemirror/state';

/**
 * Keys that typically end a word in prose. Narrow filter so we do not schedule
 * idle work on every keypress.
 */
export function isWordTerminatorKey(event: KeyboardEvent): boolean {
	if (event.metaKey || event.ctrlKey || event.altKey) {
		return false;
	}
	if (event.key === 'Enter') {
		return true;
	}
	if (event.key.length === 1) {
		return /[\s.,;:!?)\]}"'»«/\\]/u.test(event.key);
	}
	return false;
}

/**
 * After a terminator was typed, the word that was just completed is the last
 * token before trailing separators in the current text block (cursor position).
 */
export function getCompletedWord(state: EditorState): string | null {
	const { $from } = state.selection;
	if (!$from.parent.isTextblock || $from.parentOffset === 0) {
		return null;
	}

	const textBefore = $from.parent.textBetween(0, $from.parentOffset, undefined, '\ufffc');
	const withoutTrailingSeparators = textBefore.replace(/[\s\p{P}]+$/u, '');
	if (!withoutTrailingSeparators) {
		return null;
	}

	const words = withoutTrailingSeparators.split(/[\s\p{P}]+/u);
	const last = words[words.length - 1];
	if (!last || !/^[\p{L}\p{N}_]+$/u.test(last)) {
		return null;
	}

	return last;
}

/**
 * Whether the word is a keyword trigger (case-insensitive). Accepts a set or array of
 * lowercased keywords (as produced by the trigger-keyword getters).
 */

export function isKeywordTrigger(word: string, keywordSet: readonly string[]): boolean {
	return keywordSet.includes(word.toLowerCase());
}

const TRAILING_SEP_RE = /[\s\p{P}]+$/u;
const BOUNDARY_RE = /[\s\p{P}]/u;

/**
 * Checks whether the text immediately before the cursor (ignoring trailing
 * separators) ends with any keyword from the set.  Handles both single-word
 * keywords ("summary") and multi-word phrases ("next steps", "final thoughts").
 *
 * A keyword is considered matched only when it appears at a word boundary —
 * either it consumes the entire trimmed text, or the character preceding the
 * keyword is whitespace / punctuation.
 *
 * Only extracts the trailing portion of the text block (bounded by the longest
 * keyword + a small separator margin) so cost is independent of paragraph length.
 */
export function matchesKeywordTrigger(state: EditorState, keywords: readonly string[]): boolean {
	const { $from } = state.selection;
	if (!$from.parent.isTextblock || $from.parentOffset === 0) {
		return false;
	}

	let maxKwLen = 0;
	for (let i = 0; i < keywords.length; i++) {
		if (keywords[i].length > maxKwLen) {
			maxKwLen = keywords[i].length;
		}
	}

	// +1 for the word-boundary char we need to inspect; +20 covers trailing
	// separators stripped before matching.
	const extractFrom = Math.max(0, $from.parentOffset - maxKwLen - 21);
	const tail = $from.parent.textBetween(extractFrom, $from.parentOffset, undefined, '\ufffc');

	const trimmed = tail.replace(TRAILING_SEP_RE, '');
	if (!trimmed) {
		return false;
	}

	const lower = trimmed.toLowerCase();

	for (let i = 0; i < keywords.length; i++) {
		const kw = keywords[i];
		if (lower === kw) {
			return true;
		}
		if (
			lower.length > kw.length &&
			lower.endsWith(kw) &&
			BOUNDARY_RE.test(lower[lower.length - kw.length - 1])
		) {
			return true;
		}
	}
	return false;
}
